import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { pageColors } from "../mock";
import { ArrowLeft, ExternalLink, Instagram, Globe, Mail, Phone, Pencil, Check, Upload } from "lucide-react";
import Portfolio from "../components/Portfolio";
import { OrderHistory } from "../components/OrderHistory";

const AVATAR_MAX = 800 * 1024; // 800KB

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const MemberProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { users, current, updateProfile } = useAuth();
  const member = users.find((u) => u.id === id);
  const isMine = current && member && current.id === member.id;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(member || {});
  const [avatarErr, setAvatarErr] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (member) document.title = `TMM|${member.name}`;
  }, [member]);

  React.useEffect(() => { setForm(member || {}); }, [member]);

  if (!member) {
    return (
      <>
        <Header hoverColor={pageColors.contact} compact={false} />
        <div className="profile-page">
          <button className="article-back" onClick={() => navigate("/members")}>
            <ArrowLeft size={18} /> Members
          </button>
          <p className="members-empty">This profile doesn’t exist or was removed.</p>
        </div>
      </>
    );
  }

  const save = async () => {
    setSaving(true);
    await updateProfile({
      name: form.name,
      expertise: form.expertise,
      bio: form.bio,
      location: form.location || "",
      website: form.website,
      instagram: form.instagram,
      twitter: form.twitter,
      phone: form.phone,
      avatar: form.avatar,
      notify_email: form.notify_email !== false,
      notify_sms: form.notify_sms !== false,
    });
    setSaving(false);
    setEditing(false);
  };

  const onAvatar = async (e) => {
    setAvatarErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > AVATAR_MAX) {
      setAvatarErr("Please choose an image under 800 KB.");
      return;
    }
    const data = await readFileAsDataURL(file);
    setForm((f) => ({ ...f, avatar: data }));
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <Header hoverColor={pageColors.contact} compact={false} />
      <div className="profile-page">
        <button className="article-back" onClick={() => navigate("/members")}>
          <ArrowLeft size={18} /> Members
        </button>

        <div className="profile-hero">
          <div className="profile-avatar">
            {(editing ? form.avatar : member.avatar) || member.picture
              ? <img src={(editing ? form.avatar : member.avatar) || member.picture} alt={member.name} />
              : <div className="profile-avatar-fallback">{(member.name || member.email)[0].toUpperCase()}</div>}
            {editing && isMine && (
              <label className="profile-avatar-edit" data-testid="profile-avatar-upload">
                <Upload size={14} />
                <span>Change</span>
                <input type="file" accept="image/*" onChange={onAvatar} hidden />
              </label>
            )}
          </div>
          <div className="profile-head">
            {editing
              ? <input className="profile-name-input" value={form.name || ""} onChange={set("name")} />
              : <h1 className="profile-name">{member.name}</h1>}
            {editing
              ? <input className="profile-expertise-input" value={form.expertise || ""} onChange={set("expertise")} placeholder="Expertise" />
              : <div className="profile-expertise">{member.expertise || "member"}</div>}
            {isMine && (
              <button
                className="btn-ghost"
                onClick={editing ? save : () => setEditing(true)}
                disabled={saving}
                data-testid="profile-edit-btn"
              >
                {editing ? <><Check size={16}/> {saving ? "Saving…" : "Save"}</> : <><Pencil size={16}/> Edit profile</>}
              </button>
            )}
            {avatarErr && <div className="auth-error">{avatarErr}</div>}
          </div>
        </div>

        <div className="profile-body">
          <div className="profile-section">
            <div className="profile-label">Bio</div>
            {editing
              ? <textarea rows={4} className="profile-input" value={form.bio || ""} onChange={set("bio")} />
              : <p className="profile-bio">{member.bio || "No bio yet."}</p>}
          </div>

          <div className="profile-section">
            <div className="profile-label">Based in</div>
            {editing ? (
              <input
                className="profile-input"
                value={form.location || ""}
                onChange={set("location")}
                placeholder="City, Country — updates the members map"
                data-testid="profile-location-input"
              />
            ) : (
              <p className="profile-bio">{member.location_display || member.location || "Location not set."}</p>
            )}
          </div>

          <div className="profile-section">
            <div className="profile-label">Contact &amp; links</div>            <ul className="profile-links">
              <li><Mail size={16}/> <a href={`mailto:${member.email}`}>{member.email}</a></li>
              {(editing || member.phone) && (
                <li><Phone size={16}/>
                  {editing
                    ? <input className="profile-input" value={form.phone || ""} onChange={set("phone")} placeholder="+1 555…" />
                    : member.phone}
                </li>
              )}
              {(editing || member.website) && (
                <li><Globe size={16}/>
                  {editing
                    ? <input className="profile-input" value={form.website || ""} onChange={set("website")} placeholder="https://…" />
                    : <a href={member.website} target="_blank" rel="noopener noreferrer">{member.website} <ExternalLink size={14}/></a>}
                </li>
              )}
              {(editing || member.instagram) && (
                <li><Instagram size={16}/>
                  {editing
                    ? <input className="profile-input" value={form.instagram || ""} onChange={set("instagram")} placeholder="@handle" />
                    : <a href={`https://instagram.com/${(member.instagram || "").replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer">{member.instagram}</a>}
                </li>
              )}
              {(editing || member.twitter) && (
                <li><ExternalLink size={16}/>
                  {editing
                    ? <input className="profile-input" value={form.twitter || ""} onChange={set("twitter")} placeholder="Other socials" />
                    : <span>{member.twitter}</span>}
                </li>
              )}
            </ul>
          </div>

          {isMine && (
            <div className="profile-section" data-testid="profile-notifications">
              <div className="profile-label">Daily recap notifications</div>
              <p className="gd-upload-note">One daily recap of new chats and updates from the members page.</p>
              <div className="shoot-type-row">
                <label className="req-backdrop-toggle" data-testid="notify-email-toggle">
                  <input type="checkbox" checked={form.notify_email !== false}
                    disabled={!editing}
                    onChange={(e) => setForm((f) => ({ ...f, notify_email: e.target.checked }))} />
                  <span>Email me</span>
                </label>
                <label className="req-backdrop-toggle" data-testid="notify-sms-toggle">
                  <input type="checkbox" checked={form.notify_sms !== false}
                    disabled={!editing}
                    onChange={(e) => setForm((f) => ({ ...f, notify_sms: e.target.checked }))} />
                  <span>Text me{!member.phone && !form.phone ? " (add a phone number above)" : ""}</span>
                </label>
              </div>
              {!editing && <p className="gd-upload-note">Hit the edit pencil to change these.</p>}
            </div>
          )}
        </div>

        <Portfolio userId={member.id} isMine={!!isMine} />

        {isMine && <OrderHistory />}
      </div>
    </>
  );
};

export default MemberProfile;
